import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { DEFAULT_MEDIA_FILTERS, ReelMediaElement, ReelMediaFilters, ReelMediaFit } from '../reelCanvas.types';
import { mediaFilterCss } from '../reelCanvasStyles';
import { LabeledSlider } from '../controls/EffectFields';
import { ReelMediaPicker, selectionToMediaPatch } from './ReelMediaPicker';

export interface MediaElementDialogProps {
    open: boolean;
    element: ReelMediaElement | null;
    /** Open straight into the asset picker (used for a fresh media component). */
    startWithPicker?: boolean;
    onClose: () => void;
    /** Live updates while the dialog is open (the canvas previews immediately). */
    onChange: (patch: Partial<ReelMediaElement>) => void;
}

const FILTER_PRESETS: ReadonlyArray<{ label: string; value: Partial<ReelMediaFilters> }> = [
    { label: 'None', value: {} },
    { label: 'Vivid', value: { saturate: 150, contrast: 110 } },
    { label: 'Warm', value: { sepia: 25, saturate: 120 } },
    { label: 'Cool', value: { hueRotate: 200, saturate: 110 } },
    { label: 'B&W', value: { grayscale: 100, contrast: 110 } },
    { label: 'Vintage', value: { sepia: 60, contrast: 90, brightness: 105 } },
    { label: 'Faded', value: { opacity: 70, contrast: 90 } },
    { label: 'Dramatic', value: { contrast: 140, brightness: 90, saturate: 80 } },
    { label: 'Dreamy', value: { blur: 2, brightness: 110, saturate: 120 } },
    { label: 'Negative', value: { invert: 100 } },
];

/**
 * Media SideKick settings: choose / replace the published asset, fit mode (stretch breaks the
 * aspect ratio), playback flags for video, and colour filters with live preview.
 */
export const MediaElementDialog: React.FC<MediaElementDialogProps> = ({ open, element, startWithPicker = false, onClose, onChange }) => {
    const theme = useTheme();
    const [pickerOpen, setPickerOpen] = useState(false);

    useEffect(() => {
        if (open) setPickerOpen(startWithPicker);
    }, [open, startWithPicker]);

    if (!element) return null;
    const filters = { ...DEFAULT_MEDIA_FILTERS, ...element.filters };
    const setFilter = (patch: Partial<ReelMediaFilters>) => onChange({ filters: { ...filters, ...patch } });
    const hasAsset = Boolean(element.assetUrl);

    // A fresh component: only the picker is shown; selecting closes everything.
    const pickerOnly = startWithPicker && !hasAsset;

    return (
        <>
            <Dialog open={open && !pickerOnly} onClose={onClose} fullWidth maxWidth="md">
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
                    <PermMediaOutlinedIcon color="secondary" />
                    <Typography variant="h6" component="span" sx={{ fontWeight: 700, flex: 1 }} noWrap>
                        {element.name}
                    </Typography>
                    <IconButton size="small" aria-label="Close media settings" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 2.5 }}>
                    {/* Preview + asset */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box
                            sx={{
                                aspectRatio: `${element.width} / ${element.height}`,
                                maxHeight: 320,
                                borderRadius: 1.5,
                                overflow: 'hidden',
                                border: `1px solid ${theme.palette.divider}`,
                                backgroundColor: alpha('#000', 0.6),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {hasAsset ? (
                                element.mediaKind === 'video' ? (
                                    <video
                                        key={element.assetUrl ?? ''}
                                        src={element.assetUrl ?? undefined}
                                        muted
                                        playsInline
                                        preload="metadata"
                                        controls
                                        style={{ width: '100%', height: '100%', objectFit: element.fit, filter: mediaFilterCss(filters) }}
                                    />
                                ) : (
                                    <img
                                        src={element.assetUrl ?? undefined}
                                        alt={element.assetName ?? ''}
                                        style={{ width: '100%', height: '100%', objectFit: element.fit, filter: mediaFilterCss(filters) }}
                                    />
                                )
                            ) : (
                                <Typography variant="body2" color="rgba(255,255,255,0.7)">
                                    No media selected
                                </Typography>
                            )}
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" noWrap title={element.assetName ?? ''}>
                                {element.assetName ?? 'No asset'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {hasAsset
                                    ? `${element.mediaKind === 'video' ? 'Video' : 'Image'} · ${element.source === 'ae' ? 'Asset Management' : 'Beam on Screen'} library`
                                    : 'Pick a published image or video from your asset libraries.'}
                            </Typography>
                        </Box>
                        <Button variant="contained" color="secondary" startIcon={<PermMediaOutlinedIcon />} onClick={() => setPickerOpen(true)}>
                            {hasAsset ? 'Change media' : 'Choose media'}
                        </Button>
                        {hasAsset && (
                            <Button
                                variant="text"
                                color="error"
                                size="small"
                                onClick={() =>
                                    onChange({ assetId: null, assetUrl: null, publishedRelativeUrl: null, assetName: null, mediaKind: null, mimeType: null })
                                }
                            >
                                Remove media
                            </Button>
                        )}
                    </Box>

                    {/* Settings */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                Fit
                            </Typography>
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={element.fit}
                                onChange={(_e, v) => v && onChange({ fit: v as ReelMediaFit })}
                                aria-label="Media fit"
                            >
                                <ToggleButton value="fill">
                                    <Tooltip title="Stretch to the container (aspect ratio can be broken by resizing)">
                                        <span>Stretch</span>
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton value="cover">
                                    <Tooltip title="Fill the container, cropping edges to keep the aspect ratio">
                                        <span>Cover</span>
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton value="contain">
                                    <Tooltip title="Show the whole media inside the container, letterboxed">
                                        <span>Contain</span>
                                    </Tooltip>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        {element.mediaKind === 'video' && (
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <FormControlLabel
                                    control={<Switch size="small" color="secondary" checked={element.videoLoop} onChange={(e) => onChange({ videoLoop: e.target.checked })} />}
                                    label="Loop inside the clip"
                                />
                                <FormControlLabel
                                    control={<Switch size="small" color="secondary" checked={element.videoMuted} onChange={(e) => onChange({ videoMuted: e.target.checked })} />}
                                    label="Muted"
                                />
                            </Box>
                        )}

                        <Divider />

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle2">Colour filters</Typography>
                            <Tooltip title="Reset filters">
                                <IconButton size="small" aria-label="Reset filters" onClick={() => onChange({ filters: { ...DEFAULT_MEDIA_FILTERS } })}>
                                    <RestartAltIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {FILTER_PRESETS.map((p) => (
                                <Button
                                    key={p.label}
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => onChange({ filters: { ...DEFAULT_MEDIA_FILTERS, ...p.value } })}
                                    sx={{ textTransform: 'none', py: 0.25 }}
                                >
                                    {p.label}
                                </Button>
                            ))}
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2.5, rowGap: 0.5 }}>
                            <LabeledSlider label="Brightness" value={filters.brightness} min={0} max={200} unit="%" onChange={(v) => setFilter({ brightness: v })} />
                            <LabeledSlider label="Contrast" value={filters.contrast} min={0} max={200} unit="%" onChange={(v) => setFilter({ contrast: v })} />
                            <LabeledSlider label="Saturation" value={filters.saturate} min={0} max={200} unit="%" onChange={(v) => setFilter({ saturate: v })} />
                            <LabeledSlider label="Hue rotate" value={filters.hueRotate} min={0} max={360} unit="°" onChange={(v) => setFilter({ hueRotate: v })} />
                            <LabeledSlider label="Grayscale" value={filters.grayscale} min={0} max={100} unit="%" onChange={(v) => setFilter({ grayscale: v })} />
                            <LabeledSlider label="Sepia" value={filters.sepia} min={0} max={100} unit="%" onChange={(v) => setFilter({ sepia: v })} />
                            <LabeledSlider label="Invert" value={filters.invert} min={0} max={100} unit="%" onChange={(v) => setFilter({ invert: v })} />
                            <LabeledSlider label="Blur" value={filters.blur} min={0} max={20} step={0.5} unit="px" onChange={(v) => setFilter({ blur: v })} />
                            <LabeledSlider label="Opacity" value={filters.opacity} min={0} max={100} unit="%" onChange={(v) => setFilter({ opacity: v })} />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="secondary" onClick={onClose}>
                        Done
                    </Button>
                </DialogActions>
            </Dialog>

            <ReelMediaPicker
                open={open && pickerOpen}
                initialSource={element.source ?? undefined}
                onCancel={() => {
                    setPickerOpen(false);
                    if (pickerOnly) onClose();
                }}
                onSelect={(sel) => {
                    onChange(selectionToMediaPatch(sel));
                    setPickerOpen(false);
                    // Fresh component: the requirement is picker → close → canvas shows the media.
                    if (pickerOnly) onClose();
                }}
            />
        </>
    );
};

export default MediaElementDialog;
